import React from 'react';
import { Box, Container, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';

import { useSite } from '../site/SiteProvider';
import './legal.css';

/**
 * PRIVACIDAD Y AVISO LEGAL — las dos páginas que el sitio ya enlazaba y que no
 * existían.
 *
 * 🔴 CÓMO SE DESTAPÓ (29-ago-2026). Al poner la casilla de consentimiento en el
 * registro hacía falta un sitio a donde enlazar, y ahí apareció que **cuatro
 * enlaces del sitio ya apuntaban a `/privacidad` y esa ruta no estaba en el
 * Router**: los dos del pie de página, uno del registro y otro de la pantalla de
 * entrar. Llevaban a una página en blanco desde siempre.
 *
 * Y con la casilla puesta el problema pasaba de feo a inaceptable: **no se puede
 * pedir a nadie que acepte lo que no puede leer.**
 *
 * ⚠️⚠️ ESTO NO SUSTITUYE A UN ABOGADO, y está dicho en la propia página. Lo que
 * hay aquí es lo que hoy es VERDAD y se puede comprobar en el código: qué datos
 * se piden, para qué, dónde viven y cómo se borran. Un texto legal completo —el
 * responsable del tratamiento, la base jurídica, los plazos, el encargado, las
 * transferencias— lo tiene que revisar quien sepa, y hace falta antes de abrir
 * en Europa (jeshua.es). Ver `memory/fundacion_estructura_legal.md`.
 *
 * Se escribe en un solo componente con dos caras porque las dos páginas dicen
 * cosas contiguas y separarlas obligaría a repetir los datos de la Fundación.
 */

const LegalPage = ({ cara = 'privacidad' }) => {
  const sitio = useSite();
  const esPrivacidad = cara === 'privacidad';

  return (
    <Box className="leg">
      <Container maxWidth="sm">
        <Typography variant="h1" className="leg-h1">
          {esPrivacidad ? 'Cómo tratamos tus datos' : 'Aviso legal'}
        </Typography>

        {/* 🔴 SIN NEGRITAS (Faina, 29-ago: «podemos darle más sobriedad a esto;
            mucha negrita. Hazlo como en aviso legal»).
            Había once en la página, y ese es justo el problema: cuando todo se
            destaca, nada destaca — el ojo deja de saber qué mirar y el texto pasa
            de tranquilo a insistente. En una página donde alguien viene a
            comprobar qué pasa con lo suyo, la insistencia se lee como que algo
            se quiere colar.
            Los rótulos naranjas ya estructuran la página, y en las listas cada
            punto ya va separado por su viñeta: la negrita no aportaba nada que no
            estuviera dicho. */}
        {esPrivacidad ? (
          <>
            {/* Sin resumen de entrada (Faina, 29-ago). Decía «en una frase: te
                pedimos tu correo para escribirte y tu nombre para que el texto
                pueda llamarte» — y la lista de debajo lo dice ya, punto por
                punto y en cuatro renglones. Resumir cuatro líneas antes de
                escribirlas es hacer leer dos veces lo mismo. */}
            <Typography className="leg-rotulo">Qué te pedimos y para qué</Typography>
            {/* 🔴 UNA LÍNEA CADA UNA (Faina, 29-ago). Cada punto llevaba su
                explicación detrás y ocupaba dos o tres renglones: la lista se
                leía como cuatro párrafos en vez de como una lista, y el ojo ya
                no la recorría de un vistazo — que es para lo que sirve una
                lista. Lo que se pide y para qué cabe en un renglón. */}
            <Box component="ul" className="leg-lista">
              <li>Tu nombre, o cómo quieres que te llamemos, para el texto.</li>
              <li>Cómo quieres que te hablen: en masculino, en femenino o sin género.</li>
              <li>Tu correo, para avisarte y para que puedas volver a entrar.</li>
              <li>Tu contraseña, que se guarda cifrada y nadie puede leer.</li>
            </Box>

            <Typography className="leg-rotulo">Qué NO hacemos</Typography>
            <Box component="ul" className="leg-lista">
              <li>No vendemos ni cedemos tus datos a nadie.</li>
              <li>No te escribimos para venderte cosas de terceros.</li>
              {/* Sin la explicación de la pasarela (Faina, 29-ago): la frase que
                  importa es «no guardamos el número de tu tarjeta»; el cómo es
                  cosa nuestra y alargaba el punto tres veces. */}
              <li>No guardamos el número de tu tarjeta y tampoco tus datos bancarios.</li>
            </Box>

            {/* 🔴 EL BLOQUE QUE FALTABA, Y ERA EL MEJOR (29-ago). La página no
                decía nada de rastreo, y resulta que el sitio no tiene NINGUNO:
                comprobado en el código, cero analítica, cero cookies de
                terceros, cero píxeles. Y las tipografías se sirven desde el
                propio servidor a propósito, así que ni Google sabe quién entra.
                En un sitio que pide el correo de alguien que llega dolido, eso
                vale más que cualquier párrafo jurídico — y callarlo era regalar
                la mejor razón para confiar. */}
            <Typography className="leg-rotulo">No te seguimos</Typography>
            {/* Una línea cada una, igual que la lista de arriba (Faina, 29-ago).
                Lo que se cae son las explicaciones —«no sabemos qué lees», «al
                leer, tu visita no se le cuenta a nadie»—: eran la consecuencia
                de lo que ya dice el punto, y en una lista la consecuencia se
                entiende sola. */}
            <Box component="ul" className="leg-lista">
              <li>No usamos analítica ni rastreadores.</li>
              <li>No hay cookies de terceros ni píxeles de redes sociales.</li>
              <li>Las tipografías están en nuestro servidor, no en el de Google.</li>
            </Box>

            <Typography className="leg-rotulo">Lo que sí guarda tu navegador</Typography>
            <Typography className="leg-parrafo">
              {/* 🔴 UNA SOLA ENUMERACIÓN (Faina, 29-ago: «esa coma se puede sacar
                  o cómo puedes traducir ese párrafo para que suene continuo»).

                  La coma estaba ahí porque la frase tenía DOS «y»: uno dentro de
                  la enumeración —«el tamaño de letra Y la alineación»— y otro
                  para añadir la sesión, así que el segundo necesitaba una coma
                  delante o los dos se confundían.
                  Se quita el «y» de en medio y quedan cuatro cosas en fila, con
                  un solo «y» al final: la frase corre sin frenos.
                  Lo mismo se hizo antes con el inciso «—si entras—» y con la coma
                  de «y se borra». */}
              Tu preferencia de fondo claro u oscuro, el tamaño de letra, la alineación
              que hayas elegido para leer y tu sesión si entras. Se queda en tu equipo,
              no viaja a ningún sitio y se borra vaciando los datos del navegador.
            </Typography>

            <Typography className="leg-rotulo">Lo que puedes hacer</Typography>
            <Typography className="leg-parrafo">
              Desde <RouterLink to="/cuenta">tu cuenta</RouterLink> puedes cambiar tus
              datos, descargarlos todos en un archivo, o borrar la cuenta{' '}
              entera. No hay que explicar por qué ni escribir a nadie para pedirlo: son
              tuyos y la salida está a la vista.
            </Typography>

            <Typography className="leg-rotulo">Quién responde</Typography>
            <Typography className="leg-parrafo">
              {sitio.legalName}{sitio.taxId ? `, ${sitio.taxId}` : ''}
              {sitio.address ? ` · ${sitio.address}` : ''}.
              Escribe a <a href={`mailto:${sitio.email}`}>{sitio.email}</a> para
              cualquier cosa relacionada con tus datos.
            </Typography>
          </>
        ) : (
          <>
            <Typography className="leg-entrada">
              Este sitio pertenece a {sitio.legalName}.
            </Typography>

            <Typography className="leg-rotulo">Quiénes somos</Typography>
            <Typography className="leg-parrafo">
              {sitio.legalName}{sitio.taxId ? `, ${sitio.taxId}` : ''}
              {sitio.address ? `, con domicilio en ${sitio.address}` : ''}.
              Contacto: <a href={`mailto:${sitio.email}`}>{sitio.email}</a>.
            </Typography>

            {/* 🔴 EL CORPUS NO SE PUBLICA (Faina, 29-ago). El aviso legal es
                donde un canalizador mira qué hacemos con su obra, y decía solo
                que la «estudiamos y citamos» — faltaba lo que él decidió hoy:
                que el cuerpo de canalizaciones de terceros es de uso interno y
                no se distribuye. */}
            <Typography className="leg-rotulo">Las canalizaciones</Typography>
            {/* El texto es de Faina (29-ago), literal. Dos afinados suyos sobre
                la primera versión, y los dos corrigen algo que era inexacto:
                  · «canalizaciones PROPIAS y de terceros» — las piezas no salen
                    solo de leer a otros: Faina canaliza, y sus propios cuerpos
                    son la columna vertebral de muchas. Decir solo «de terceros»
                    borraba al canalizador de la casa.
                  · fuera «que estudiamos y citamos», que describía el método
                    interno donde solo hacía falta decir de dónde vienen. */}
            <Typography className="leg-parrafo">
              Los mensajes que publicamos son obra de la Fundación y se basan en
              canalizaciones propias y de terceros. Puedes leerlos, compartirlos e
              imprimirlos para tu uso. No está permitido venderlos ni publicarlos como
              propios.
            </Typography>
            {/* 🔴 SIN EL PARRAFO DEL CORPUS (Faina, 29-ago): «no creo que debamos
                ponerlo, porque no vamos a publicar textos de terceros y solo lo
                vamos a utilizar como material interno. Si en el futuro queremos
                publicar algo del corpus, lo haremos con la autorización de los
                canalizadores.»

                Y el razonamiento se sostiene: si no se distribuye obra ajena, no
                hay nada que declarar ni que ofrecer retirar. Lo único de terceros
                que llega al público son las CITAS dentro de nuestros textos, y
                eso ya lo dice el párrafo de arriba —«se basan en canalizaciones
                de terceros que estudiamos y citamos»—.

                ⚠️ Si algún día se publica material del corpus, este párrafo tiene
                que volver, y con la autorización delante. */}

            {/* 🔴 SIMPLE, Y EN PRIMERA PERSONA (Faina, 29-ago: «hazlo de nuevo,
                pero algo simple: No somos una fundación que da consejos…»).

                La versión anterior definía qué NO es una canalización, en tercera
                persona y con cuatro negaciones seguidas. Sonaba a descargo de
                responsabilidad. Esta dice quiénes NO somos NOSOTROS, que es lo
                que el rótulo promete, y las mismas reglas duras del proyecto
                —sin profecías, sin juicios, sin decirle a nadie qué hacer— caben
                en una frase corta cada una.
                ⭐ Y el remate es suyo (29-ago): «Publicamos mensajes y tú debes
                decidir qué hacer con ellos. Te sugerimos que les hagas caso.»
                Lo mío decía «qué haces con ellos es tuyo», que era neutro y se
                lavaba las manos. El suyo hace las dos cosas a la vez: deja la
                decisión entera al lector Y toma partido por lo que publica.
                Una fundación que no cree en sus propios mensajes no tendría por
                qué publicarlos. */}
            <Typography className="leg-rotulo">Qué no somos</Typography>
            <Typography className="leg-parrafo">
              {/* Sin negrita (Faina, 29-ago): el rótulo ya dice «Qué no somos»,
                  así que destacar la primera frase era repetir el título en
                  negro sobre el párrafo. */}
              No somos una fundación que da consejos. No te decimos qué hacer con
              tu vida, no anunciamos el futuro y no juzgamos a nadie. Publicamos mensajes
              y tú debes decidir qué hacer con ellos. Te sugerimos que les hagas caso.
            </Typography>
            {/* ⚠️ AQUI IBA EL DESCARGO —«no es consejo médico, psicológico, legal
                ni financiero; si necesitas ayuda profesional, búscala: un texto
                no la sustituye»— y Faina pidió borrarlo el 29-ago.

                Queda anotado porque es una decisión con consecuencia, no un
                recorte de estilo: el sitio publica piezas tituladas «No puedo con
                el dolor», «La noche oscura del alma» o «Perdí a alguien», así que
                parte de quien lee está pasándolo mal de verdad. Esa línea era la
                única del sitio que le decía que buscara ayuda.
                Si algún día se repone, su sitio es este. */}

            <Typography className="leg-rotulo">Las donaciones</Typography>
            <Typography className="leg-parrafo">
              {/* Def 102 (Faina, 14-10-2026): todo lo que recibe la Fundación son donaciones. */}
              El mensaje del día es gratis. Las donaciones todavía no están abiertas: cuando lo
              estén, aquí dirá quién las recibe, qué recibe antes quien dona como
              agradecimiento, cómo se detiene una donación y en qué casos se devuelve.
            </Typography>

            <Typography className="leg-rotulo">Tus datos</Typography>
            <Typography className="leg-parrafo">
              Están explicados aparte, en{' '}
              <RouterLink to="/privacidad">cómo tratamos tus datos</RouterLink>.
            </Typography>
          </>
        )}

        {/* ⚠️ Dicho en la propia página, no escondido en un comentario del
            código: quien lee esto tiene derecho a saber qué está leyendo. */}
        {/* 🔴 CADA PAGINA DICE LO QUE LE FALTA A ELLA (29-ago). Las dos llevaban
            la misma nota, la de privacidad: hablaba de plazos de conservación y
            del derecho a reclamar ante la autoridad de protección de datos, que
            en el aviso legal no pinta nada. Lo que falta aquí es otra cosa —los
            datos del vehículo español y las condiciones de venta—.
            La fecha importa en las dos más que en ninguna otra página: quien
            vuelve a leerlas quiere saber si cambió algo desde la última vez. */}
        <Typography className="leg-pendiente">
          Última revisión: 29 de agosto de 2026. Esta página dice lo que hoy es cierto y
          se puede comprobar.{' '}
          {esPrivacidad
            ? 'El texto legal definitivo —con los plazos de conservación, la base '
              + 'jurídica y el derecho a reclamar ante la autoridad de protección de '
              + 'datos— está pendiente de revisión antes de abrir jeshua.es.'
            : 'Faltan los datos del vehículo europeo y las condiciones de las '
              + 'donaciones, que se añadirán antes de abrir jeshua.es y antes de '
              + 'recibir la primera donación.'}
        </Typography>
      </Container>
    </Box>
  );
};

export default LegalPage;
